import axiosInstance from '@api/axios';
import { urls } from '@api/urls';

/**
 * The firm template library (ZLAYT-1639, ZLAYT-1679): one live Word template
 * per firm and letter type. Every route answers 404 while the server's
 * TEMPLATE_DRAFTING_ENABLED switch is off.
 */

export interface LetterType {
  id: number;
  name: string;
}

/** A letter type with the firm's live template for it, or null. */
export interface LetterTypeChoice extends LetterType {
  template_id: number | null;
}

export interface MergeField {
  name: string;
  label: string;
  kind: string;
}

export interface LetterTemplateVersion {
  id: number;
  number: number;
  original_filename: string;
  size: number;
  merge_fields: MergeField[];
  origin: 'admin_upload' | 'converted_draft' | 'uploaded_example';
  /** Email of who saved it, or null when that account is gone. */
  created_by: string | null;
  created_at: string;
}

export interface LetterTemplate {
  id: number;
  letter_type: LetterType;
  current_version: LetterTemplateVersion | null;
  can_manage: boolean;
  created_at: string;
  updated_at: string;
}

/** What the types call says: switched off (404), or the list. */
export type LetterTemplateTypes =
  | { off: true }
  | { off: false; canManage: boolean; types: LetterTypeChoice[] };

/**
 * The active letter types and whether the caller may add, replace and
 * delete. A 404 is asked for as a normal answer: the shared axios instance
 * rejects with the response body alone, so a rejected 404 cannot be told
 * apart from any other failure afterwards.
 */
export const getLetterTemplateTypes =
  async (): Promise<LetterTemplateTypes> => {
    const response = await axiosInstance.get(urls.letterTemplates.types, {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    });
    if (response.status === 404) return { off: true };
    return {
      off: false,
      canManage: Boolean(response.data?.can_manage),
      types: (response.data?.types ?? []) as LetterTypeChoice[],
    };
  };

export const listLetterTemplates = async (): Promise<LetterTemplate[]> => {
  const response = await axiosInstance.get(urls.letterTemplates.base);
  return response.data as LetterTemplate[];
};

const fileBody = (file: File, extra: Record<string, string> = {}) => {
  const data = new FormData();
  Object.entries(extra).forEach(([key, value]) => data.append(key, value));
  data.append('file', file);
  return data;
};

const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

/** Start the firm's template for a type that has none. */
export const addLetterTemplate = async (
  letterTypeId: number,
  file: File
): Promise<LetterTemplate> => {
  const response = await axiosInstance.post(
    urls.letterTemplates.base,
    fileBody(file, { letter_type: String(letterTypeId) }),
    MULTIPART
  );
  return response.data as LetterTemplate;
};

/** Replace a template: adds a version and makes it the live one. */
export const replaceLetterTemplate = async (
  templateId: number,
  file: File
): Promise<LetterTemplate> => {
  const response = await axiosInstance.post(
    urls.letterTemplates.versions(templateId),
    fileBody(file),
    MULTIPART
  );
  return response.data as LetterTemplate;
};

/** Take a template out of the library; the server keeps its versions. */
export const deleteLetterTemplate = async (
  templateId: number
): Promise<void> => {
  await axiosInstance.delete(urls.letterTemplates.byId(templateId));
};

/** The field names the server puts in front of a refusal ("file: ..."). */
const FIELD_PREFIX = /^(file|letter_type):\s*/;

const nonEmpty = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * What to tell the user about a failed call: the server's own reason, as it
 * gave it, without the field name in front ("file: ...") that only means
 * something to a developer. The shared axios instance rejects with the
 * response body alone.
 */
export function templateErrorMessage(
  err: unknown,
  fallback: string,
  options: { notFound?: string } = {}
): string {
  if (!err || typeof err !== 'object') return fallback;
  const body = err as { error?: unknown; detail?: unknown };
  const envelope = body.error as
    | { message?: unknown; details?: unknown; code?: unknown }
    | undefined;
  if (envelope && typeof envelope === 'object') {
    if (options.notFound && envelope.code === 'not_found') {
      return options.notFound;
    }
    if (Array.isArray(envelope.details) && envelope.details.length) {
      return envelope.details
        .map((line) => String(line).replace(FIELD_PREFIX, ''))
        .join(' ');
    }
    if (nonEmpty(envelope.message)) {
      return envelope.message.replace(FIELD_PREFIX, '');
    }
  }
  if (nonEmpty(body.detail)) return body.detail;
  return fallback;
}
